import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, Trash2, ClipboardCopy, Search, 
  Info, Bookmark, Printer, AlertTriangle,
  Download, ArrowRight, Phone, User, FileText
} from 'lucide-react';
import { useSavedFoundations } from '../context/SavedFoundationsContext';
import { useSubscription } from '../context/SubscriptionContext';
import { Foundation } from '../types/Foundation';
import { useFoundationDetails } from '../hooks/useFoundationDetails';

export function FoundationsManager() {
  const { 
    savedFoundations, 
    removeFoundation, 
    clearFoundations,
    maxFoundations
  } = useSavedFoundations();
  
  // State for limit notification
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [limitWarningMessage, setLimitWarningMessage] = useState('');
  
  const { } = useSubscription();
  
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('list');
  const [exportFormat, setExportFormat] = useState<'text' | 'phoneList' | 'nameList' | 'csv'>('text');
  const [copied, setCopied] = useState(false);
  const [selectedFoundation, setSelectedFoundation] = useState<Foundation | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  
  // Use the foundation details hook
  const { fetchFoundationDetails } = useFoundationDetails();
  
  // Prevent background scrolling when the modal is open
  React.useEffect(() => {
    if (isOpen) {
      // Save the current scroll position
      const scrollY = window.scrollY;
      
      // Add styles to prevent scrolling on the body
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      return () => {
        // Remove the styles and restore scroll position when modal closes
        document.body.style.overflow = originalStyle;
        document.documentElement.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);
  
  // Format a foundation's data for export
  const formatFoundationData = (foundation: Foundation): string => {
    const lines: string[] = [];
    
    lines.push(`Namn: ${foundation.name}`);
    if (foundation.purpose) lines.push(`Ändamål: ${foundation.purpose}`);
    if (foundation.region) lines.push(`Ort: ${foundation.region}`);
    if (foundation.address) lines.push(`Adress: ${foundation.address}`);
    if (foundation.contactInfo) lines.push(`Kontakt: ${foundation.contactInfo}`);
    
    return lines.join('\n');
  };
  
  // Generate exportable data from all saved foundations
  const generateExport = (): string => {
    if (savedFoundations.length === 0) return 'Inga sparade stiftelser.';
    
    const lines: string[] = [];
    lines.push(`SPARADE STIFTELSER (${savedFoundations.length})`);
    lines.push('============================');
    
    savedFoundations.forEach((foundation, index) => {
      lines.push(`\n[${index + 1}] ${foundation.name}`);
      lines.push(formatFoundationData(foundation));
      lines.push('----------------------------');
    });
    
    return lines.join('\n');
  };
  
  // Generate phone list from foundations
  const generatePhoneList = (): string => {
    if (savedFoundations.length === 0) return 'Inga sparade stiftelser.';
    
    const lines: string[] = [];
    lines.push(`TELEFONNUMMER (${savedFoundations.length} stiftelser)`);
    lines.push('============================');
    
    savedFoundations.forEach((foundation, index) => {
      lines.push(`\n[${index + 1}] ${foundation.name}`);
      
      // Extract phone from contactInfo if exists (basic pattern matching)
      if (foundation.contactInfo) {
        const phonePattern = /(?:tel|telefon|phone|mobil)?:?\s*(?:\+46|0)[\d\s-]{7,}/gi;
        const matches = foundation.contactInfo.match(phonePattern);
        
        if (matches && matches.length > 0) {
          matches.forEach(match => {
            lines.push(`Tel: ${match.trim()}`);
          });
        } else {
          lines.push('Tel: Inget telefonnummer tillgängligt');
        }
      } else {
        lines.push('Tel: Inget telefonnummer tillgängligt');
      }
      
      lines.push('----------------------------');
    });
    
    return lines.join('\n');
  };
  
  // Generate name list from foundations
  const generateNameList = (): string => {
    if (savedFoundations.length === 0) return 'Inga sparade stiftelser.';
    
    const lines: string[] = [];
    lines.push(`NAMNLISTA (${savedFoundations.length} stiftelser)`);
    lines.push('============================');
    
    savedFoundations.forEach((foundation, index) => {
      lines.push(`\n[${index + 1}] ${foundation.name}`);
      
      if (foundation.board && foundation.board.length > 0) {
        foundation.board.forEach(member => {
          lines.push(`${member.role}: ${member.name}`);
        });
      } else {
        lines.push('Inga styrelsemedlemmar tillgängliga');
      }
      
      lines.push('----------------------------');
    });
    
    return lines.join('\n');
  };
  
  // Generate CSV data
  const generateCSV = (): string => {
    if (savedFoundations.length === 0) return 'Inga sparade stiftelser.';
    
    const headers = ['Namn', 'Ändamål', 'Region', 'Adress', 'Kontaktinformation'];
    const rows = [headers.join(',')];
    
    savedFoundations.forEach(foundation => {
      const row = [
        `"${foundation.name.replace(/"/g, '""')}"`,
        `"${foundation.purpose ? foundation.purpose.replace(/"/g, '""') : ''}"`,
        `"${foundation.region ? foundation.region.replace(/"/g, '""') : ''}"`,
        `"${foundation.address ? foundation.address.replace(/"/g, '""') : ''}"`,
        `"${foundation.contactInfo ? foundation.contactInfo.replace(/"/g, '""') : ''}"`,
      ];
      rows.push(row.join(','));
    });
    
    return rows.join('\n');
  };
  
  // Get current export data based on format
  const getCurrentExportData = (): string => {
    switch (exportFormat) {
      case 'phoneList':
        return generatePhoneList();
      case 'nameList':
        return generateNameList();
      case 'csv':
        return generateCSV();
      case 'text':
      default:
        return generateExport();
    }
  };
  
  // Copy to clipboard
  const copyToClipboard = () => {
    const exportData = getCurrentExportData();
    navigator.clipboard.writeText(exportData);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Print current export data
  const printExportData = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const exportData = getCurrentExportData();
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Sparade Stiftelser</title>
            <style>
              body { font-family: Arial, sans-serif; line-height: 1.6; padding: 20px; }
              pre { white-space: pre-wrap; }
              @media print {
                body { padding: 0; }
                button { display: none; }
              }
            </style>
          </head>
          <body>
            <button onclick="window.print()">Skriv ut</button>
            <button onclick="window.close()">Stäng</button>
            <pre>${exportData}</pre>
          </body>
        </html>
      `);
      
      printWindow.document.close();
    }
  };
  
  // Download as text file
  const downloadTextFile = () => {
    const exportData = getCurrentExportData();
    const blob = new Blob([exportData], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    
    let filename = 'stiftelser';
    switch (exportFormat) {
      case 'phoneList':
        filename = 'stiftelser-telefonnummer';
        break;
      case 'nameList':
        filename = 'stiftelser-namnlista';
        break;
      case 'csv':
        filename = 'stiftelser-data.csv';
        break;
      default:
        filename = 'stiftelser-export.txt';
    }
    
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Filter foundations by search term
  const filteredFoundations = searchTerm.trim() 
    ? savedFoundations.filter(f => 
        f.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (f.purpose && f.purpose.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (f.region && f.region.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : savedFoundations;

  // Toggle the manager open/closed
  const toggleManager = () => {
    setIsOpen(!isOpen);
  };

  // Register the event listeners
  React.useEffect(() => {
    // Listen for toggle event
    window.addEventListener('toggleFoundationsManager', toggleManager);
    
    // Listen for limit reached event
    const handleLimitReached = (event: CustomEvent<{message: string}>) => {
      setShowLimitWarning(true);
      setLimitWarningMessage(event.detail.message);
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setShowLimitWarning(false);
      }, 5000);
    };
    
    window.addEventListener('limitReached', handleLimitReached as EventListener);
    
    return () => {
      window.removeEventListener('toggleFoundationsManager', toggleManager);
      window.removeEventListener('limitReached', handleLimitReached as EventListener);
    };
  }, [isOpen]);

  // Foundation card component
  const FoundationCard = ({ foundation }: { foundation: Foundation }) => (
    <div className="bg-white p-3 rounded-lg shadow hover:shadow-md transition-shadow border border-gray-100">
      <div className="flex justify-between">
        <h4 className="font-medium text-blue-600">{foundation.name}</h4>
        <button
          onClick={() => removeFoundation(foundation.name)}
          className="text-gray-400 hover:text-red-500 transition-colors"
          aria-label="Ta bort"
        >
          <Trash2 size={16} />
        </button>
      </div>
      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
        {foundation.purpose}
      </p>
      {foundation.region && (
        <p className="text-xs text-gray-500 mt-1">
          {foundation.region}
        </p>
      )}
      <div className="flex justify-end mt-2">
        <button 
          onClick={async () => {
            setDetailsError(null);
            setSelectedFoundation(foundation);
            setActiveTab('details');
            setIsLoadingDetails(true);
            
            try {
              // Fetch complete foundation details from database
              const completeData = await fetchFoundationDetails(foundation);
              
              if (completeData) {
                // Update with the complete data that includes all fields
                setSelectedFoundation(completeData);
              } else {
                setDetailsError('Kunde inte hämta fullständig information');
              }
            } catch (error) {
              setDetailsError(error instanceof Error ? error.message : 'Ett fel uppstod');
            } finally {
              setIsLoadingDetails(false);
            }
          }}
          className="px-2 py-1 bg-blue-500 text-white text-xs rounded flex items-center gap-1 hover:bg-blue-600 transition-colors"
        >
          Hämta fullständig information <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );

  // Button component for tab navigation
  const TabButton = ({ id, label, icon }: { id: string, label: string, icon: React.ReactNode }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
        activeTab === id
          ? 'bg-blue-500 text-white'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  // Export format button
  const ExportFormatButton = ({ format, label, icon }: { format: typeof exportFormat, label: string, icon: React.ReactNode }) => (
    <button
      onClick={() => setExportFormat(format)}
      className={`px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
        exportFormat === format
          ? 'bg-blue-500 text-white'
          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  // Floating button to open the manager
  const FloatingButton = () => (
    <div className="fixed bottom-6 right-6 z-40">
      <button
        onClick={toggleManager}
        className="bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg 
                  hover:shadow-xl transition-all duration-300 flex items-center gap-2"
      >
        <Bookmark size={20} />
        <span className="font-medium">{savedFoundations.length > 0 ? `${savedFoundations.length} sparade` : 'Mina stiftelser'}</span>
      </button>
    </div>
  );

  // If not open, just render the floating button
  if (!isOpen) {
    return <FloatingButton />;
  }

  // The main manager content when open
  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      {/* Limit warning notification */}
      {showLimitWarning && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-md shadow-md z-50 max-w-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <Info size={20} className="text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{limitWarningMessage || `Du kan endast spara upp till ${maxFoundations} stiftelser.`}</p>
              <p className="text-xs mt-1">Ta bort några stiftelser innan du lägger till fler.</p>
            </div>
            <button
              onClick={() => setShowLimitWarning(false)}
              className="ml-auto -mx-1.5 -my-1.5 bg-yellow-100 text-yellow-500 rounded-md p-1.5 focus:outline-none"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-xl">
          <h2 className="text-white font-bold text-xl flex items-center gap-2">
            <Bookmark />
            Mina sparade stiftelser
            <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
              {savedFoundations.length}
            </span>
          </h2>
          <button 
            onClick={() => setIsOpen(false)}
            className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Stäng"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* No global premium banner as per feedback - moved to details view */}
        
        {/* Main Content */}
        <div className="flex-grow overflow-hidden flex flex-col md:flex-row p-4 sm:p-6 gap-4 sm:gap-6">
          {/* Tabs for navigation (mobile only) */}
          <div className="md:hidden flex overflow-x-auto gap-2 pb-4">
            <TabButton id="list" label="Lista" icon={<Bookmark size={16} />} />
            <TabButton id="export" label="Exportera" icon={<Download size={16} />} />
            {selectedFoundation && (
              <TabButton id="details" label="Detaljer" icon={<Info size={16} />} />
            )}
          </div>
          
          {/* Left panel - Foundation List */}
          <div className={`${activeTab !== 'list' ? 'hidden md:block' : ''} md:w-1/2 flex flex-col overflow-hidden`}>
            <div className="mb-4 flex flex-col sm:flex-row justify-between gap-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Sök bland sparade stiftelser..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 pl-9 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={clearFoundations}
                  disabled={savedFoundations.length === 0}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-1"
                >
                  <Trash2 size={14} />
                  Rensa alla
                </button>
                
                <button
                  onClick={() => {
                    setActiveTab('export');
                    setExportFormat('text');
                  }}
                  disabled={savedFoundations.length === 0}
                  className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm md:hidden flex items-center gap-1"
                >
                  <Download size={14} />
                  Exportera
                </button>
              </div>
            </div>

            {/* List of saved foundations */}
            {savedFoundations.length === 0 ? (
              <div className="flex-grow flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg text-gray-500">
                <Bookmark size={48} className="text-gray-300 mb-4" />
                <p className="text-center font-medium">Inga sparade stiftelser än.</p>
                <p className="text-sm text-center mt-2 max-w-md">
                  Spara stiftelser genom att klicka på "Spara" i sökresultaten. 
                  Dina sparade stiftelser visas här för enkel åtkomst.
                </p>
              </div>
            ) : filteredFoundations.length === 0 ? (
              <div className="flex-grow flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg text-gray-500">
                <Search size={48} className="text-gray-300 mb-4" />
                <p className="text-center font-medium">Inga matchande stiftelser.</p>
                <p className="text-sm text-center mt-2">
                  Din sökning gav inga resultat. Prova med andra söktermer.
                </p>
              </div>
            ) : (
              <div className="flex-grow overflow-y-auto pr-2">
                <div className="grid grid-cols-1 gap-3">
                  {filteredFoundations.map((foundation) => (
                    <FoundationCard key={foundation.name} foundation={foundation} />
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Right panel - Export/Details */}
          <div className={`${(activeTab !== 'export' && activeTab !== 'details') ? 'hidden md:block' : ''} md:w-1/2 overflow-hidden flex flex-col`}>
            {/* Export section */}
            {(activeTab === 'export' || (!selectedFoundation && activeTab !== 'details')) && (
              <div className="bg-gray-50 rounded-lg p-5 h-full flex flex-col">
                <h3 className="font-semibold text-gray-800 mb-4">Exportera stiftelser</h3>
                
                {/* Export format selection */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <ExportFormatButton 
                    format="text" 
                    label="Fullständig text" 
                    icon={<FileText size={16} />} 
                  />
                  <ExportFormatButton 
                    format="phoneList" 
                    label="Telefonnummer" 
                    icon={<Phone size={16} />} 
                  />
                  <ExportFormatButton 
                    format="nameList" 
                    label="Namnlista" 
                    icon={<User size={16} />} 
                  />
                  <ExportFormatButton 
                    format="csv" 
                    label="CSV-format" 
                    icon={<FileText size={16} />}
                  />
                </div>
                
                {/* Export action buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setShowPreview(true)}
                    disabled={savedFoundations.length === 0}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center gap-1"
                  >
                    <Search size={16} />
                    Förhandsgranska
                  </button>
                  
                  <div className="ml-auto flex items-center gap-2">
                    {showPreview && (
                      <>
                        <button
                          onClick={copyToClipboard}
                          className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm flex items-center gap-1"
                        >
                          <ClipboardCopy size={16} />
                          {copied ? 'Kopierat!' : 'Kopiera'}
                        </button>
                        
                        <button
                          onClick={downloadTextFile}
                          className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm flex items-center gap-1"
                        >
                          <Download size={16} />
                          Ladda ner
                        </button>
                        
                        <button
                          onClick={printExportData}
                          className="px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm flex items-center gap-1"
                        >
                          <Printer size={16} />
                          Skriv ut
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Premium Feature Section replacing preview area */}
                {!showPreview ? (
                    <div className="bg-white p-3 rounded-lg border-2 border-blue-200 flex-grow flex flex-col">
                      <div className="bg-blue-50 px-3 py-1.5 flex justify-between items-center rounded-md mb-2">
                        <h3 className="text-sm font-semibold text-blue-700">Djup sökning</h3>
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Premium</span>
                      </div>
                      
                      <div className="bg-gray-50 p-2 rounded-lg border border-gray-100 text-xs sm:text-sm flex-1 overflow-y-auto max-h-[200px] sm:max-h-[240px]">
                      <p className="text-gray-700 mb-1 text-xs">Med premium får du bl.a tillgång till vår DeepSearch:</p>
                      <p className="text-gray-700 mb-1 text-xs leading-tight">
                        Övergripande djupsökning från ett 30-tal olika källor på nätet för att hitta 
                        kontaktuppgifter, webadresser, email och ansökningstider. Samt aktivitetsstatus.
                      </p>
                      <div className="bg-green-50 rounded p-1 text-xs text-green-700 mb-1">
                        För extra synlighet prova gärna Projektbörsen också, funktionen är gratis!
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-2 mt-3">
                        <a 
                          href="/premium"
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 rounded-md flex items-center justify-center gap-1 font-medium text-xs sm:text-sm"
                        >
                          <Search size={14} />
                          Aktivera djupsökning
                        </a>
                        <a 
                          href="/projektborsen"
                          className="flex-1 bg-green-500 hover:bg-green-600 text-white py-1 px-2 rounded-md flex items-center justify-center gap-1 font-medium text-xs sm:text-sm"
                        >
                          Projektbörsen
                        </a>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-5 rounded-lg border border-gray-200 flex-grow flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-medium text-gray-700">
                        {exportFormat === 'text' && 'Förhandsgranskning - Fullständig information'}
                        {exportFormat === 'phoneList' && 'Förhandsgranskning - Telefonnummer'}
                        {exportFormat === 'nameList' && 'Förhandsgranskning - Namnlista'}
                        {exportFormat === 'csv' && 'Förhandsgranskning - CSV-format'}
                      </h4>
                      <button 
                        onClick={() => setShowPreview(false)}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        aria-label="Stäng förhandsgranskning"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    
                    <div className="bg-gray-50 p-3 sm:p-4 rounded-md border border-gray-200 flex-grow overflow-y-auto max-h-[250px] sm:max-h-[300px] font-mono text-sm text-gray-700 whitespace-pre-wrap">
                      {getCurrentExportData()}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Details section */}
            {activeTab === 'details' && selectedFoundation && (
              <div className="bg-white rounded-lg border border-gray-200 p-5 h-full flex flex-col overflow-y-auto">
                {isLoadingDetails ? (
                  <div className="flex flex-col items-center justify-center h-full py-12">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-4"></div>
                    <p className="text-gray-500">Hämtar fullständig information...</p>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-semibold text-blue-600 text-lg">{selectedFoundation.name}</h3>
                      <button 
                        onClick={() => {
                          setSelectedFoundation(null);
                          setActiveTab('list');
                        }}
                        className="text-gray-400 hover:text-gray-600 p-1"
                        aria-label="Stäng"
                      >
                        <X size={16} />
                      </button>
                    </div>
                    
                    {detailsError ? (
                      <div className="bg-red-50 p-3 rounded-lg mb-4 text-sm text-red-600 flex items-center gap-2">
                        <AlertTriangle size={16} />
                        <p>Ett fel uppstod: {detailsError}</p>
                      </div>
                    ) : (
                      <div className="bg-blue-50 p-3 rounded-lg mb-4 text-sm text-blue-600 flex items-center gap-2">
                        <Info size={16} />
                        <p>Databasen uppdaterad. Fullständig information hämtad.</p>
                      </div>
                    )}
                    
                    {/* Removed top premium banner as per request */}
                    
                    {/* Basic information */}
                    <div className="mb-6">
                      <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3 pb-1 border-b border-gray-200">
                        Grundläggande Information
                      </h3>
                      
                      {selectedFoundation.purpose && (
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Ändamål</h4>
                          <p className="text-gray-800">{selectedFoundation.purpose}</p>
                        </div>
                      )}
                      
                      {selectedFoundation.region && (
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Region</h4>
                          <p className="text-gray-800">{selectedFoundation.region}</p>
                        </div>
                      )}
                      
                      {selectedFoundation.address && (
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Adress</h4>
                          <p className="text-gray-800">{selectedFoundation.address}</p>
                        </div>
                      )}
                      
                      {selectedFoundation.contactInfo && (
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Kontaktinformation</h4>
                          <p className="text-gray-800 whitespace-pre-line">{selectedFoundation.contactInfo}</p>
                        </div>
                      )}
                      
                      {selectedFoundation.yearEstablished && (
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Etablerad</h4>
                          <p className="text-gray-800">{selectedFoundation.yearEstablished}</p>
                        </div>
                      )}
                      
                      {selectedFoundation.capital && (
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Kapital</h4>
                          <p className="text-gray-800">{selectedFoundation.capital.toLocaleString('sv-SE')} SEK</p>
                        </div>
                      )}
                      
                      {/* Premium fields with special styling */}
                      <div className="mb-4 pb-3 border-b border-gray-100">
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1 flex items-center">
                          Email
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Premium</span>
                        </h4>
                        <p className="text-gray-500 italic">{selectedFoundation.email || 'Kräver Premium - Djupsökning på Webben'}</p>
                      </div>
                      
                      <div className="mb-4 pb-3 border-b border-gray-100">
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1 flex items-center">
                          Hemsida
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Premium</span>
                        </h4>
                        <p className="text-gray-500 italic">{selectedFoundation.website || 'Kräver Premium - Djupsökning på Webben'}</p>
                      </div>
                      
                      <div className="mb-4 pb-3 border-b border-gray-100">
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-1 flex items-center">
                          Ansökningsdatum
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Premium</span>
                        </h4>
                        <p className="text-gray-500 italic">{selectedFoundation.applicationDate || 'Kräver Premium - Djupsökning på Webben'}</p>
                      </div>
                    </div>
                    
                    {/* Governance information */}
                    {selectedFoundation.foundationData && (
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3 pb-1 border-b border-gray-200">
                          Styrning
                        </h3>
                        
                        {selectedFoundation.board && selectedFoundation.board.length > 0 && (
                          <div className="mb-4 pb-3 border-b border-gray-100">
                            <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Styrelse</h4>
                            <ul className="text-gray-800 list-disc list-inside">
                              {selectedFoundation.board.map((member, index) => (
                                <li key={index} className="mb-1">
                                  <span className="font-medium">{member.role}:</span> {member.name}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Stadgar</h4>
                          <p className="text-gray-800">{selectedFoundation.foundationData.governance.statutes}</p>
                        </div>
                        
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Beslutsfattande</h4>
                          <p className="text-gray-800">{selectedFoundation.foundationData.governance.decisionMaking}</p>
                        </div>
                        
                        {selectedFoundation.firmaTeckning && (
                          <div className="mb-4 pb-3 border-b border-gray-100">
                            <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Firmateckning</h4>
                            <p className="text-gray-800">{selectedFoundation.firmaTeckning}</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Financial information */}
                    {selectedFoundation.foundationData && (
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3 pb-1 border-b border-gray-200">
                          Finansiell Information
                        </h3>
                        
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Räkenskapsperiod</h4>
                          <p className="text-gray-800">{selectedFoundation.foundationData.financial.accountingPeriod}</p>
                        </div>
                        
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Revisionskrav</h4>
                          <p className="text-gray-800">{selectedFoundation.foundationData.financial.auditRequirements}</p>
                        </div>
                        
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Placeringspolicy</h4>
                          <p className="text-gray-800">{selectedFoundation.foundationData.financial.investmentPolicy}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Legal information */}
                    {selectedFoundation.foundationData && (
                      <div className="mb-6">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase mb-3 pb-1 border-b border-gray-200">
                          Juridisk Information
                        </h3>
                        
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Registreringsdetaljer</h4>
                          <p className="text-gray-800">{selectedFoundation.foundationData.legal.registrationDetails}</p>
                        </div>
                        
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Skattestatus</h4>
                          <p className="text-gray-800">{selectedFoundation.foundationData.legal.taxStatus}</p>
                        </div>
                        
                        <div className="mb-4 pb-3 border-b border-gray-100">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-1">Efterlevnadskrav</h4>
                          <p className="text-gray-800">{selectedFoundation.foundationData.legal.complianceRequirements}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Premium Feature Section with enhanced description */}
                    <div className="mt-6 mb-4 border-2 border-blue-200 rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-4 py-2 flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-blue-700">Djup sökning</h3>
                        <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded">Premium</span>
                      </div>
                      
                      <div className="p-4 bg-white">
                        <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-100 text-sm overflow-y-auto max-h-[200px] sm:max-h-[250px]">
                          <p className="text-gray-700 mb-1 text-xs">Med premium får du bl.a tillgång till vår DeepSearch:</p>
                          <p className="text-gray-700 mb-1 text-xs leading-tight">
                            Övergripande djupsökning från ett 30-tal olika källor på nätet för att hitta 
                            kontaktuppgifter, webadresser, email och ansökningstider. Samt aktivitetsstatus.
                          </p>
                          <div className="bg-green-50 rounded p-1 text-xs text-green-700 mb-1">
                            För extra synlighet prova gärna Projektbörsen också, funktionen är gratis!
                          </div>
                          
                          <div className="flex flex-col sm:flex-row gap-2 mt-3">
                            <a 
                              href="/premium"
                              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-1 px-2 rounded-md flex items-center justify-center gap-1 font-medium text-xs sm:text-sm"
                            >
                              <Search size={14} />
                              Aktivera djupsökning
                            </a>
                            <a 
                              href="/projektborsen"
                              className="flex-1 bg-green-500 hover:bg-green-600 text-white py-1 px-2 rounded-md flex items-center justify-center gap-1 font-medium text-xs sm:text-sm"
                            >
                              Projektbörsen
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-auto pt-4 flex justify-end">
                      <button
                        onClick={() => {
                          removeFoundation(selectedFoundation.name);
                          setSelectedFoundation(null);
                          setActiveTab('list');
                        }}
                        className="px-4 py-2 bg-red-50 text-red-600 rounded-md hover:bg-red-100 flex items-center gap-2"
                      >
                        <Trash2 size={16} />
                        Ta bort från sparade
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
